import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FavoriteButton from "./FavoriteButton";
import { ShareDealButton } from "./ShareDealButton";
import MarketSignalBadge from "./MarketSignalBadge";
import { buildCohortSubtitle } from "../utils/marketSignal";
import { resolveInsightText } from "../utils/insight";
import { enumLabel, featureLabel } from "../utils/enumLabels";
import { useAuth } from "../auth/AuthContext";
import { PaywallModal } from "./billing/PaywallComponents";
import { hasValidUrl, shouldShowUnverifiedBadge } from "../utils/image";
import { useImageFallback } from "../hooks/useImageFallback";
import { RentEstimateChips } from "./RentEstimateInfo";

/* -------- helpers -------- */
function ImageHealthBadge({ image_health }) {
  if (image_health === "alive" || !image_health) return null;
  if (image_health === "unchecked") {
    return (
      <div
        data-testid="image-health-badge-unchecked"
        title="Image not yet verified"
        className="absolute top-2 right-2 h-6 px-2 rounded-full bg-zinc-900/80 text-zinc-300 text-xs flex items-center border border-zinc-700"
      >
        ? unverified
      </div>
    );
  }
  return (
    <div
      data-testid="image-health-badge-dead"
      title="Listing image broken — original ad may still be live"
      className="absolute top-2 right-2 h-6 px-2 rounded-full bg-rose-500/20 text-rose-300 text-xs flex items-center border border-rose-500/40"
    >
      ⚠ broken image
    </div>
  );
}

function GenerationChip({ series_match_level, model_series }) {
  if (!model_series) return null;
  if (series_match_level === "exact_generation") {
    return (
      <span
        data-testid="gen-chip-exact"
        className="ml-2 px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[10px] uppercase tracking-wide"
      >
        ✓ Generation
      </span>
    );
  }
  if (series_match_level === "family_only") {
    return (
      <span
        data-testid="gen-chip-family"
        className="ml-2 px-2 py-0.5 rounded bg-zinc-700/40 text-zinc-400 text-[10px] uppercase tracking-wide"
      >
        Generation approx.
      </span>
    );
  }
  return null;
}

function Placeholder({ image_health, hasUrls }) {
  return (
    <div className="absolute inset-0 grid place-items-center bg-zinc-900 text-zinc-600">
      <div className="text-center">
        <div className="text-4xl mb-2 opacity-30">◾</div>
        <div className="text-xs uppercase tracking-widest">
          {image_health === "dead_on_check" && !hasUrls ? "image broken" : "no image"}
        </div>
      </div>
    </div>
  );
}

const RISK_SEVERITY = {
  leasehold: "red",
  heritage_protection: "red",
  rented_indefinitely: "orange",
  foreclosure: "orange",
  renovation_needed: "orange",
  tenancy_risk: "orange",
  // Master-Audit 2026-07 — auto risk flags now render through the SAME
  // curated badge (translated), instead of the old raw-slug chips.
  not_accident_free: "orange",
  price_on_request: "orange",
};

// P1.4 (2026-07-16) — warn badges (mapper-extracted risk flags).
function RiskBadges({ card, t }) {
  const flags = Array.isArray(card.risk_flags)
    ? card.risk_flags.filter((f) => RISK_SEVERITY[f])
    : [];
  if (!flags.length) return null;
  return (
    <div
      className="mt-2 flex flex-wrap gap-1.5"
      data-testid={`deal-card-risk-badges-${card.id}`}
    >
      {flags.map((f) => {
        const red = RISK_SEVERITY[f] === "red";
        return (
          <span
            key={f}
            data-testid={`risk-badge-${f}`}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
              red
                ? "border-rose-500/50 bg-rose-950/60 text-rose-200"
                : "border-amber-500/50 bg-amber-950/50 text-amber-200"
            }`}
          >
            <span aria-hidden="true">{red ? "🔴" : "🟠"}</span>
            {t(`risk.${f}`)}
          </span>
        );
      })}
    </div>
  );
}

function PriceChangeBadge({ price_change }) {  if (!price_change || !price_change.direction) return null;
  const isDown = price_change.direction === "down";
  const isUp = price_change.direction === "up";
  // Format delta — round to nearest €100 if >= €1000, otherwise exact.
  const delta = price_change.delta_eur || 0;
  const formatted =
    delta >= 1000
      ? `€${Math.round(delta / 100) * 100 / 1}`.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.")
      : `€${Math.round(delta).toLocaleString("de-AT")}`;
  const days = price_change.days || 0;
  if (isDown) {
    return (
      <div
        data-testid="price-change-badge-down"
        title={`Price dropped from €${Math.round(price_change.from_price).toLocaleString("de-AT")} to €${Math.round(price_change.to_price).toLocaleString("de-AT")} over the last ${days} day${days === 1 ? "" : "s"}`}
        className="absolute bottom-2 right-2 h-6 px-2 rounded-full bg-rose-500 text-zinc-950 text-xs font-medium flex items-center shadow-lg"
      >
        ↓ {formatted}
      </div>
    );
  }
  if (isUp) {
    return (
      <div
        data-testid="price-change-badge-up"
        title={`Price rose from €${Math.round(price_change.from_price).toLocaleString("de-AT")} to €${Math.round(price_change.to_price).toLocaleString("de-AT")} over the last ${days} day${days === 1 ? "" : "s"}`}
        className="absolute bottom-2 right-2 h-6 px-2 rounded-full bg-zinc-800/90 text-zinc-400 text-[10px] flex items-center border border-zinc-700"
      >
        ↑ {formatted}
      </div>
    );
  }
  return null;
}

/* -------- card body renderers -------- */
function AutoCardMeta({ card }) {
  const bits = [];
  if (card.year) bits.push(card.year);
  if (card.mileage_km != null)
    bits.push(`${card.mileage_km.toLocaleString("de-AT")} km`);
  if (card.fuel) bits.push(enumLabel("fuel", card.fuel));
  if (card.gearbox) bits.push(enumLabel("gearbox", card.gearbox));
  return (
    <div className="text-xs text-zinc-400 mt-2 flex flex-wrap gap-x-2">
      {bits.map((b, i) => (
        <span key={i}>
          {i > 0 && <span className="text-zinc-700 mr-2">•</span>}
          {b}
        </span>
      ))}
    </div>
  );
}

function ImmoCardMeta({ card }) {
  const bits = [];
  if (card.transaction_type)
    bits.push(enumLabel("transaction_type", card.transaction_type));
  // Phase 1 Quick-Wins: nie `"unknown"` als sichtbaren Text rendern.
  // Apify liefert für ~97% der frischen Listings keinen PROPERTY_TYPE →
  // Adapter setzt `object_type_canonical = "unknown"`. Frontend hat das
  // bisher truthy-getestet (`if (card.object_type)`) und den String dem
  // User gezeigt. Hier explizit ausschließen.
  if (card.object_type && card.object_type !== "unknown") {
    bits.push(enumLabel("object_type", card.object_type));
  }
  if (card.living_area_m2 != null) bits.push(`${card.living_area_m2} m²`);
  // Apify liefert NUMBER_OF_ROOMS=0 für Neubau-Projekt-Listings (kein
  // konkretes Wohnungsdetail). `to_float()` macht daraus 0.0, was bisher
  // mit `!= null` truthy war → "0 Zi" wurde angezeigt. Jetzt nur rendern
  // wenn echter Wert > 0.
  if (card.rooms != null && card.rooms > 0) bits.push(`${card.rooms} Zi`);
  return (
    <div className="text-xs text-zinc-400 mt-2 flex flex-wrap gap-x-2">
      {bits.map((b, i) => (
        <span key={i}>
          {i > 0 && <span className="text-zinc-700 mr-2">•</span>}
          {b}
        </span>
      ))}
    </div>
  );
}

/* -------- main component -------- */
export default function DealCard({ card, isFavorite = false, expanded = false }) {
  const { t } = useTranslation("listing");
  const vertical = card.vertical;
  const navigate = useNavigate();
  const { isPro, isAdmin } = useAuth();
  const [paywallOpen, setPaywallOpen] = useState(false);
  // Phase H2-followup round-8 — runtime image-fallback. Hero starts as
  // `pickHeroUrl(card)` (unchanged selection logic) and auto-advances
  // through the rest of `collectImageUrls()` on `<img onError>`. Keeps
  // the placeholder for the all-failed case.
  const { currentUrl: heroUrl, onError: handleImgError } = useImageFallback(card);
  const showImage = hasValidUrl(heroUrl);
  // Mobile-Web-Parity Cycle: image-health overlays (`? unverified`,
  // `⚠ broken image`) are admin-only. Non-admins never see this jargon —
  // the placeholder still kicks in for missing images (handled by
  // `showImage`), but no diagnostic chip is drawn on top.
  const showUnverifiedBadge =
    isAdmin && shouldShowUnverifiedBadge(card.image_health) && showImage;

  // Phase D + E — "KI fragen" deep-link button. Two paths:
  //   - Pro user → navigates to detail page with `?ai=open` so the
  //     advisor panel scrolls into view + glows briefly.
  //   - Non-pro → opens the paywall modal in-place; never burns a
  //     server round-trip on a user that can't actually use the
  //     advisor.
  const handleAskAi = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPro) {
      setPaywallOpen(true);
      return;
    }
    navigate(`/listing/${card.id}?ai=open`);
  };

  // When `expanded=true` (Top Deals section), surface the cohort detail
  // INLINE (not just in the tooltip): "{N} Vergleichsangebote · {band}".
  // Phase H2-followup round-7 — also surface the cohort line on the
  // regular feed when a caveat is present, so the count + cohort scope
  // ("185 Vergleiche · Bundesland / national · nur breite
  // Vergleichsbasis") sits right under the badge and the user never
  // has to mentally reconcile "geringe Datenbasis" with a 3-digit count.
  const showCohortLine =
    (expanded || !!card.market_signal_caveat)
    && card.comparable_count != null
    && card.comparable_count > 0;
  const cohortSubtitleText = buildCohortSubtitle(card);

  // Welle 4 — "⚠ Beschädigt" directly on the card (registry verdict or
  // detected damage flags). `unknown` is NOT damage — no false alarm.
  const hasDamage =
    (card.damage_condition
      && !["healthy", "unknown"].includes(card.damage_condition))
    || (Array.isArray(card.damage_flags) && card.damage_flags.length > 0);

  // V4.13 Lifecycle — favorites keep inactive listings (history stays),
  // but the card must be honest about availability.
  const isInactive = card.status === "inactive";

  return (
    <Link
      to={`/listing/${card.id}`}
      data-testid={`deal-card-${card.id}`}
      data-vertical={vertical}
      className="group block rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950/60 hover:border-amber-500/40 transition"
    >
      <div className="relative aspect-[4/3] bg-zinc-900">
        {showImage ? (
          <img
            src={heroUrl}
            alt={card.title || ""}
            className={`absolute inset-0 w-full h-full object-cover ${isInactive ? "grayscale opacity-60" : ""}`}
            loading="lazy"
            onError={handleImgError}
          />
        ) : (
          <Placeholder image_health={card.image_health} hasUrls={false} />
        )}
        {showImage && showUnverifiedBadge ? (
          <div
            data-testid={`deal-card-unverified-badge-${card.id}`}
            className="absolute top-2 right-2 h-6 px-2 rounded-full bg-zinc-900/80 text-zinc-300 text-xs flex items-center border border-zinc-700"
          >
            ? unverified
          </div>
        ) : (
          // Image-health diagnostic chips are admin-only (Mobile-Web
          // Parity Cycle). Non-admin users never see "alive / unverified
          // / broken" jargon on the card.
          isAdmin ? <ImageHealthBadge image_health={card.image_health} /> : null
        )}
        <PriceChangeBadge price_change={card.price_change} />
        <div className="absolute top-2 left-2">
          <FavoriteButton listingUlid={card.id} isFavorite={isFavorite} size="sm" />
        </div>
        {/* Welle 4 — damage is visible ON the card, never only in the
            AI chat. Canonical registry verdict OR text damage flags. */}
        {hasDamage && (
          <div
            data-testid={`deal-card-damage-badge-${card.id}`}
            className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full border border-rose-500/50 bg-rose-950/85 px-2.5 py-1 text-[11px] font-semibold text-rose-200 backdrop-blur-sm"
          >
            <span aria-hidden="true">⚠</span>
            {card.damage_condition && !["healthy", "unknown"].includes(card.damage_condition)
              ? t(`damageBadge_${card.damage_condition}`, { defaultValue: t("damagedBadge") })
              : t("damagedBadge")}
          </div>
        )}
        {isInactive && (
          <div
            data-testid={`deal-card-inactive-badge-${card.id}`}
            className="absolute bottom-2 right-2 inline-flex items-center rounded-full border border-zinc-600/70 bg-zinc-950/85 px-2.5 py-1 text-[11px] font-semibold text-zinc-300 backdrop-blur-sm"
          >
            {t("inactiveBadge")}
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3
            className="text-zinc-100 font-medium leading-snug line-clamp-2"
            data-testid={`deal-card-title-${card.id}`}
          >
            {card.title || "(no title)"}
            {vertical === "auto" && (
              <GenerationChip
                series_match_level={card.series_match_level}
                model_series={card.model_series}
              />
            )}
          </h3>
          <div
            data-testid={`deal-card-price-${card.id}`}
            className="text-amber-400 font-medium whitespace-nowrap"
          >
            {card.price_display || "—"}
          </div>
        </div>
        {card.subtitle && (
          <div className="text-xs text-zinc-500 mt-1">{card.subtitle}</div>
        )}

        {/* P1.4 (2026-07-16) — risk warn badges, visible directly on
            the card (not only in the AI chat). */}
        <RiskBadges card={card} t={t} />

        {/* Phase Insight-V1 — feed-level 1-liner generated by the
            Insight-Worker. Sits ABOVE the market-signal badge so the
            user reads the human-language verdict first, then the
            engine's structured signal underneath. Null-safe: when the
            worker hasn't produced an insight (skipped via smart-trigger
            or generic listing), this block disappears entirely. */}
        {resolveInsightText(card) && (
          <div
            data-testid={`deal-card-ai-insight-${card.id}`}
            className="mt-2 flex items-start gap-1.5 text-[12px] leading-snug text-amber-200/90"
          >
            <span aria-hidden="true" className="text-amber-400/90 shrink-0">✦</span>
            <span className="line-clamp-3">{resolveInsightText(card)}</span>
          </div>
        )}

        {/* M8: market signal badge (strict / soft / cautious caveat). Renders
            nothing when the engine has no analysis for this listing. Sits
            just below the title+price row and above the meta line so it's
            visually prominent without crowding the price-change badge that
            lives on the image. */}
        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
          <MarketSignalBadge card={card} />
          {/* Phase D — "KI fragen" deep-link. Compact pill, lives in the
              same row as the market badge. Stops propagation so the
              outer card-Link doesn't also fire. */}
          <div className="shrink-0 flex items-center gap-1.5">
            <ShareDealButton
              listingUlid={card.id}
              variant="iconSm"
              stopPropagation
            />
            <button
              type="button"
              data-testid={`deal-card-ask-ai-${card.id}`}
              onClick={handleAskAi}
              className="shrink-0 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-[11px] font-medium inline-flex items-center gap-1 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition"
            >
              <span aria-hidden="true">✦</span>
              {t("card.askAi")}
            </button>
          </div>
        </div>

        {/* Top Deals section (expanded=true) surfaces the cohort detail
            INLINE so users see comp count + Sicherheit band without having
            to hover. The regular DealFlow grid keeps expanded=false and
            this block is omitted UNLESS a caveat is set (Ford Focus /
            Artstetten broad-cohort case — Phase H2-followup round-7). */}
        {showCohortLine && cohortSubtitleText && (
          <div
            data-testid={`deal-card-cohort-line-${card.id}`}
            className="mt-1.5 text-[11px] text-zinc-500"
          >
            {cohortSubtitleText}
          </div>
        )}

        {vertical === "auto" ? <AutoCardMeta card={card} /> : <ImmoCardMeta card={card} />}

        {/* V4.23 — central rent-estimate: yield range + legal warning
            chips on immo-buy cards (backend truth, display only). */}
        {vertical !== "auto" && <RentEstimateChips card={card} />}

        {/* Phase B (description display upgrade): a short, server-flattened
            excerpt of the listing description with "Mehr anzeigen" → detail
            page. Card stays click-through to /listing/{id}, so the
            "Mehr anzeigen" pill is purely a visual affordance — the whole
            card is already a <Link>. Skipped entirely when the listing
            has no usable description. */}
        {card.description_excerpt && (
          <div
            data-testid={`deal-card-desc-${card.id}`}
            className="mt-3 text-xs leading-relaxed text-zinc-400"
          >
            <span className="line-clamp-3">{card.description_excerpt}</span>
            <span
              data-testid={`deal-card-desc-more-${card.id}`}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-400/90 hover:text-amber-300"
            >
              {t("card.showMore")} <span aria-hidden="true">→</span>
            </span>
          </div>
        )}

        {vertical === "immo" && card.has_outdoor_space && (
          <div className="mt-2 text-xs text-emerald-300/80">🌿 {t("card.outdoor")}</div>
        )}
        {vertical === "immo" && card.feature_slugs && card.feature_slugs.length > 0 && (
          <div
            data-testid={`deal-card-immo-features-${card.id}`}
            className="mt-2 flex flex-wrap gap-1"
          >
            {card.feature_slugs.slice(0, 4).map((slug) => (
              <span
                key={slug}
                className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300/90 text-[10px]"
              >
                {featureLabel(slug)}
              </span>
            ))}
          </div>
        )}
        {/* V4.10 Phase 4 — Auto Feature-Badges: max 5 kuratierte
            High-Value-Highlights (360°-Kamera, Panoramadach, HUD …),
            übersetzt über das geteilte enums:features-Namespace. */}
        {vertical === "auto" &&
          card.feature_slugs &&
          card.feature_slugs.length > 0 && (
            <div
              data-testid={`deal-card-auto-features-${card.id}`}
              className="mt-2 flex flex-wrap gap-1"
            >
              {card.feature_slugs.slice(0, 5).map((slug) => (
                <span
                  key={slug}
                  className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300/90 text-[10px]"
                >
                  {featureLabel(slug)}
                </span>
              ))}
            </div>
          )}
        {/* Master-Audit 2026-07 — the old raw auto risk_flags chips
            (untranslated slugs, duplicating RiskBadges above) were
            removed. All risk flags render through RiskBadges. */}

        <div className="text-[10px] text-zinc-600 mt-3 flex justify-between">
          <span>{card.location || card.country || ""}</span>
          <span>{card.source_platform}</span>
        </div>
      </div>
      {/* Phase E paywall modal — only mounted when triggered by the
          non-pro "KI fragen" path. Renders as a fixed overlay; safe to
          live inside the card's <Link> wrapper because PaywallModal
          stops propagation on its own clicks. */}
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        source="dealcard_ai"
      />
    </Link>
  );
}
